/**
 * The route a signed-out visitor was going to, remembered once and resumed after signing
 * in (docs/ux/PLATFORM-ADMIN.md §18.2).
 *
 * It arrives in the URL, so it is attacker-controllable: a link to
 * `/login?next=https://elsewhere.example` that the sign-in page obeys is an open redirect
 * wearing a session, and this application's session is the whole estate. Only a path
 * **inside this application** is ever followed.
 *
 * Three forms have to be refused rather than one. An absolute URL is the obvious case; a
 * protocol-relative `//elsewhere.example` is the one a leading-slash check waves through,
 * and browsers treat it as an absolute URL to another host. The third is the **backslash**
 * — per the WHATWG URL parser a `\` is a path separator for special schemes, so
 * `/\elsewhere.example` and `/\/elsewhere.example` both normalise to
 * `https://elsewhere.example/`. It is the standard bypass for exactly this guard, and it
 * reads as an in-application path to anything that only looks at the first character.
 */
const IN_APPLICATION_PATH = /^\/(?!\/)[^\s?#\\]*$/

/**
 * `/login` is never resumed. It *is* a route in this application, so the check above
 * admits it, and following it would land a just-signed-in owner back on the sign-in form
 * — which renders perfectly well while signed in, because it hangs off the root route
 * rather than off the authenticated one. Refused here rather than fixed there: "signing
 * in takes you somewhere useful" is a property of the route being resumed.
 */
const SIGN_IN_PATH = /^\/login(?:\/|$)/i

export function safeNextPath(value: unknown): string | undefined {
  if (typeof value !== 'string' || !IN_APPLICATION_PATH.test(value) || SIGN_IN_PATH.test(value)) {
    return undefined
  }
  return value
}
